import {Component, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {FileInput, FileInputComponent, FileValidator} from "ngx-material-file-input";
import {delay, first, takeUntil} from "rxjs/operators";
import {Uuid} from "../../../shared/interface/response/uuid";
import {HttpErrorResponse} from "@angular/common/http";
import {Dictionary} from "../../../shared/interface/dictionary";
import {FormBuilder, FormControl, FormGroup, Validators} from "@angular/forms";
import {MatSnackBar} from "@angular/material/snack-bar";
import {FormHelperService} from "../../../shared/services/form-helper.service";
import {DOCUMENT} from "@angular/common";
import {ReplaySubject} from "rxjs";
import {MatDialogRef} from "@angular/material/dialog";
import {
  AUTOMATIC_PARTNER_TAGS,
  IMPORT_STATUS_ERROR,
} from "../../../shared/services/dict.service";
import {PartnerTagsImportService} from "../../../shared/services/partner-tags-import.service";
import {PartnerTagsImport} from "../../../shared/interface/partner-tags-import";
import {FileBase64} from "../../../shared/components/file-drag-and-drop/file-drag-and-drop.component";

@Component({
  selector: 'app-partner-tags-import',
  templateUrl: './partner-tags-import.component.html',
  styleUrls: ['./partner-tags-import.component.scss']
})
export class PartnerTagsImportComponent implements OnInit, OnDestroy {

  @Output() importCompleted = new EventEmitter<any>();

  readonly AUTOMATIC_PARTNER_TAGS = AUTOMATIC_PARTNER_TAGS;
  readonly IMPORT_STATUS_ERROR = IMPORT_STATUS_ERROR;

  partnerTagsImportFormValid = false;
  partnerTagsImportForm: FormGroup;
  fileControl: FormControl;
  assignTagControl: FormControl;
  synchronizeTagControl: FormControl;
  tagControl: FormControl;

  fileMaxSize = 7340032;
  processingRequest = false;
  formErrors = {};
  partnerTagsImport?: PartnerTagsImport;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  constructor(
    private fb: FormBuilder,
    private snack: MatSnackBar,
    private formHelper: FormHelperService,
    private partnerTagsImportService: PartnerTagsImportService,
    public dialogRef: MatDialogRef<PartnerTagsImportComponent>,
    @Inject(DOCUMENT) private document: Document) {
    this.partnerTagsImportForm = fb.group({
      tag: this.tagControl = new FormControl(null, [Validators.required]),
      assignTag: this.assignTagControl = fb.control(false),
      synchronizeTag: this.synchronizeTagControl = fb.control(false),
      fileContent: fb.control('', [Validators.required]),
      fileName: fb.control('', [Validators.required]),
    });

  }

  ngOnInit(): void {
    this.partnerTagsImportForm.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
        delay(0)
      )
      .subscribe(() => {
        this.partnerTagsImportFormValid = this.partnerTagsImportForm.valid;
      });

    this.assignTagControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((val: boolean) => {
        if (val) {
          this.synchronizeTagControl.setValue(false);
        }
      });

    this.synchronizeTagControl.valueChanges
      .pipe(
        takeUntil(this.destroyed$),
      )
      .subscribe((val: boolean) => {
        if (val) {
          this.assignTagControl.setValue(false);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  loadImport(importId: string): void {
    this.partnerTagsImportService.getImport(importId)
      .pipe(delay(1000))
      .subscribe((partnerTagsImport: PartnerTagsImport) => {
        this.partnerTagsImport = partnerTagsImport;

        if (partnerTagsImport.progress !== 100) {
          if (this.partnerTagsImport.status.key !== IMPORT_STATUS_ERROR){
            this.loadImport(partnerTagsImport.uuid);
          } else {
            this.snack.open(`Błąd importu: ${this.partnerTagsImport.output}`, '', {
              duration: 2000,
            });
            this.importCompleted.emit();
          }
        } else {
          this.snack.open('Import zakończony prawidłowo', '', {
            duration: 2000,
          });
          this.importCompleted.emit();
        }
      });
  }

  import(): void {
    if (!this.partnerTagsImportForm.valid) {
      this.partnerTagsImportForm.markAllAsTouched();
      this.snack.open('Wypełnij lub popraw wszystkie wymagane pola', '', {
        duration: 2000,
      });
      return;
    }

    if (!this.assignTagControl.value && !this.synchronizeTagControl.value) {
      this.snack.open('Wybierz typ akcji', '', {
        duration: 2000,
      });
      return;
    }

    this.processingRequest = true;

    this.formHelper.removeBackendErrorState(this.partnerTagsImportForm, this.formErrors);
    this.formErrors = {};

    this.partnerTagsImportService.import(this.partnerTagsImportForm.value)
      .pipe(
        first()
      )
      .subscribe((response: Uuid) => {
        this.snack.open('Rozpoczynanie importowania', '', {
          duration: 2000,
        });
        this.dialogRef.close();
        this.processingRequest = false;
        this.loadImport(response.uuid);
      }, (error: HttpErrorResponse) => {
        this.processingRequest = false;

        this.snack.open('Podczas importu wystąpił błąd', error.status.toString(), {
          duration: 2000,
        });

        if (error.status === 400) {
          this.formErrors = error.error.error.list;
          this.formHelper.setBackendErrorState(this.partnerTagsImportForm, this.formErrors);
        }
      })
    ;
  }

  copyMessage(val: string): void{
    const selBox = document.createElement('textarea');
    selBox.style.position = 'fixed';
    selBox.style.left = '0';
    selBox.style.top = '0';
    selBox.style.opacity = '0';
    selBox.value = val;
    document.body.appendChild(selBox);
    selBox.focus();
    selBox.select();
    document.execCommand('copy');
    document.body.removeChild(selBox);
    this.snack.open('Skopiowano do schowka', '', {duration: 2000});
  }

  fileSelected(file: FileBase64): void {
    this.partnerTagsImportForm.get('fileContent').setValue(file?.base64);
    this.partnerTagsImportForm.get('fileName').setValue(file?.filename);
  }

  cancel(e): void {
    this.dialogRef.close(e);
    return;
  }
}
